import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/PageHeader.jsx';
import { Field } from '@/components/Field.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import { confirmAction, showError, showSuccess } from '@/utils/alerts.js';
import { useFlota } from './hooks/useFlota.js';
import { resolveSucursalActivaId } from '@/utils/requestContext.js';

const ESTADOS = [
  { value: '', label: 'Todos' },
  { value: 'activo', label: 'Activo' },
  { value: 'inactivo', label: 'Inactivo' },
  { value: 'mantenimiento', label: 'Mantenimiento' },
];

const TIPOS = [
  { value: '', label: 'Todos' },
  { value: 'camion', label: 'Camión' },
  { value: 'utilitario', label: 'Utilitario' },
  { value: 'semi', label: 'Semi' },
  { value: 'acoplado', label: 'Acoplado' },
  { value: 'auto', label: 'Auto' },
  { value: 'moto', label: 'Moto' },
  { value: 'otro', label: 'Otro' },
];

function getEstadoStyles(estado) {
  switch (estado) {
    case 'activo':
      return 'bg-[rgba(var(--brand),0.15)] text-[rgb(var(--brand-700))]';
    case 'mantenimiento':
      return 'bg-[rgba(var(--muted),0.25)] text-[rgb(var(--fg))]';
    case 'inactivo':
      return 'bg-[rgba(var(--shadow),0.18)] text-[rgb(var(--fg))]';
    default:
      return 'bg-[rgba(var(--panel),0.6)] text-[rgb(var(--muted))]';
  }
}

export default function ListadoFlota() {
  const navigate = useNavigate();
  const { loading, error, success, list, deactivate } = useFlota();
  const activeSucursalId = resolveSucursalActivaId();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    estado: '',
    tipo: '',
    sucursal_id: String(activeSucursalId || ''),
  });

  const loadData = async (nextFilters = filters) => {
    const data = await list({
      search: nextFilters.search,
      estado: nextFilters.estado,
      tipo: nextFilters.tipo,
      sucursal_id: nextFilters.sucursal_id,
    });
    setRows(data);
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSubmitFilters = event => {
    event.preventDefault();
    loadData(filters);
  };

  const handleDeactivate = async vehiculo => {
    const decision = await confirmAction({
      title: '¿Desactivar vehículo?',
      text: `Se marcará como inactivo el vehículo ${vehiculo.patente}.`,
      confirmText: 'Desactivar',
    });
    if (!decision.isConfirmed) return;

    try {
      await deactivate(vehiculo.id, { throwOnError: true });
      await showSuccess('Listo', `El vehículo ${vehiculo.patente} fue desactivado.`);
      await loadData();
    } catch (deactivateError) {
      await showError('Error', deactivateError?.message || 'No se pudo desactivar el vehículo.');
    }
  };

  return (
    <div className="min-h-screen bg-[rgba(var(--bg),0.95)] pb-16">
      <div className="mx-auto max-w-[1400px] px-4 pb-10 pt-8">
        <PageHeader
          title="Flota"
          subtitle="Administrá los vehículos operativos de la sucursal activa y su asignación vigente."
          actions={[
            <button
              key="refresh"
              type="button"
              onClick={loadData}
              className="flex items-center gap-2 rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            >
              Actualizar
            </button>,
            <button
              key="new"
              type="button"
              onClick={() => navigate('/administracion/flota/nuevo')}
              className="flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[rgb(var(--brand-700))] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ring))]"
            >
              Nuevo vehículo
            </button>,
          ]}
        />

        <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.85)] p-6 shadow-[0_18px_34px_-20px_rgba(var(--shadow),0.45)]">
          <form onSubmit={handleSubmitFilters} className="grid grid-cols-12 gap-4">
            <Field label="Patente" hint="Buscá por patente" className="col-span-12 md:col-span-5">
              <input
                type="text"
                value={filters.search}
                onChange={event => setFilters(prev => ({ ...prev, search: event.target.value }))}
                className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                placeholder="Ej: ABC123"
              />
            </Field>
            <Field label="Tipo" className="col-span-12 md:col-span-3">
              <select
                value={filters.tipo}
                onChange={event => setFilters(prev => ({ ...prev, tipo: event.target.value }))}
                className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                {TIPOS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Estado" className="col-span-12 md:col-span-2">
              <select
                value={filters.estado}
                onChange={event => setFilters(prev => ({ ...prev, estado: event.target.value }))}
                className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                {ESTADOS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Sucursal activa" className="col-span-12 md:col-span-2">
              <div className="mt-1 flex h-10 items-center rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.78)] px-3 text-sm text-[rgb(var(--fg))]">
                {activeSucursalId ? `#${activeSucursalId}` : 'Sin resolver'}
              </div>
            </Field>
          </form>
        </section>

        {success ? (
          <div className="mt-4 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-4 py-3 text-sm text-[rgb(var(--brand-700))]">
            {success}
          </div>
        ) : null}

        <section className="mt-6 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.8)] shadow-[0_18px_34px_-20px_rgba(var(--shadow),0.45)]">
          <div className="border-b border-[rgb(var(--border))] px-6 py-4 text-sm text-[rgb(var(--muted))]">
            Vehículos registrados
          </div>
          {error ? <div className="px-6 py-4 text-sm text-[rgb(var(--danger,255_99_99))]">{error}</div> : null}
          {loading ? <div className="px-6 py-10 text-sm text-[rgb(var(--muted))]">Cargando vehículos...</div> : null}
          {!loading && rows.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title="Sin vehículos"
                description="No hay vehículos con los filtros aplicados."
                action={
                  <button
                    type="button"
                    onClick={() => navigate('/administracion/flota/nuevo')}
                    className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[rgb(var(--brand-700))] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ring))]"
                  >
                    Crear vehículo
                  </button>
                }
              />
            </div>
          ) : null}
          {!loading && rows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[rgba(var(--panel),0.95)] text-xs uppercase tracking-wide text-[rgb(var(--muted))]">
                  <tr>
                    <th className="px-6 py-3 text-left">Patente</th>
                    <th className="px-6 py-3 text-left">Tipo</th>
                    <th className="px-6 py-3 text-left">Marca / Modelo</th>
                    <th className="px-6 py-3 text-left">Estado</th>
                    <th className="px-6 py-3 text-left">Chofer</th>
                    <th className="px-6 py-3 text-left">Sucursal</th>
                    <th className="px-6 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgb(var(--border))]">
                  {rows.map(vehiculo => (
                    <tr key={vehiculo.id} className="hover:bg-[rgba(var(--muted),0.08)]">
                      <td className="px-6 py-4 font-semibold text-[rgb(var(--fg))]">{vehiculo.patente}</td>
                      <td className="px-6 py-4 text-[rgb(var(--fg))]">{vehiculo.tipo || '—'}</td>
                      <td className="px-6 py-4 text-[rgb(var(--fg))]">
                        {[vehiculo.marca, vehiculo.modelo].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center rounded-xl px-2.5 py-1 text-xs font-semibold ${getEstadoStyles(vehiculo.estado)}`}>
                          {vehiculo.estado}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-[rgb(var(--fg))]">
                        {vehiculo.chofer_apellido && vehiculo.chofer_nombre
                          ? `${vehiculo.chofer_apellido}, ${vehiculo.chofer_nombre}`
                          : vehiculo.chofer_id
                            ? `Chofer #${vehiculo.chofer_id}`
                            : 'Sin asignar'}
                      </td>
                      <td className="px-6 py-4 text-[rgb(var(--fg))]">{vehiculo.sucursal_id || '—'}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => navigate(`/administracion/flota/${vehiculo.id}/editar`)}
                            className="rounded-xl border border-[rgb(var(--border))] px-3 py-1 text-xs text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeactivate(vehiculo)}
                            className="rounded-xl border border-[rgb(var(--border))] px-3 py-1 text-xs text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                          >
                            Desactivar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
